const CACHE_KEY = 'admin_users_cache';
const CACHE_DURATION = 16 * 60 * 60 * 1000; // 16 часов

// Сохранение пользователей в кэш
export const saveToCache = (users) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      timestamp: Date.now(),
      users: users
    }));
  } catch (error) {
    console.error("Error saving to cache:", error);
  }
};

// Загрузка пользователей из кэша
export const loadFromCache = () => {
  try {
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (!cachedData) return null;
    
    const { timestamp, users } = JSON.parse(cachedData);
    if (Date.now() - timestamp < CACHE_DURATION) {
      // Валидация структуры данных перед возвратом
      if (Array.isArray(users) && users.length > 0) {
        // Убеждаемся, что все пользователи имеют поле userType
        const validatedUsers = users.map(user => ({
          ...user,
          userType: user.userType || (user.username?.startsWith('@') || user.firstName ? 'telegram' : 'web')
        }));
        return validatedUsers;
      }
      return users;
    }
  } catch (error) {
    console.error("Error loading from cache:", error);
  }
  return null;
};

// Очистка кэша
export const clearCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.error("Error clearing cache:", error);
  }
};

// Обновление конкретного пользователя в кэше
export const updateCache = (userId, updateData) => {
  try {
    const cachedUsers = loadFromCache();
    if (cachedUsers && Array.isArray(cachedUsers)) {
      const updatedUsers = cachedUsers.map(user => {
        if (user.id === userId) {
          // Объединяем старые данные с новыми
          const updatedUser = { ...user, ...updateData };
          // Сохраняем userType если он был
          if (user.userType && !updatedUser.userType) {
            updatedUser.userType = user.userType;
          }
          return updatedUser;
        }
        return user;
      });
      saveToCache(updatedUsers);
    }
  } catch (error) {
    console.error("Error updating cache:", error);
  }
};

// Добавление нового пользователя в кэш
export const addToCache = (user) => {
  try {
    const cachedUsers = loadFromCache();
    if (cachedUsers && Array.isArray(cachedUsers)) {
      // Проверяем, нет ли уже такого пользователя
      const exists = cachedUsers.some(u => u.id === user.id);
      if (!exists) {
        const updatedUsers = [...cachedUsers, user];
        saveToCache(updatedUsers);
      }
    } else if (user) {
      saveToCache([user]);
    }
  } catch (error) {
    console.error("Error adding to cache:", error);
  }
};

// Удаление пользователя из кэша
export const deleteFromCache = (userId) => {
  try {
    const cachedUsers = loadFromCache();
    if (cachedUsers && Array.isArray(cachedUsers)) {
      const updatedUsers = cachedUsers.filter(user => user.id !== userId);
      saveToCache(updatedUsers);
    }
  } catch (error) {
    console.error("Error deleting from cache:", error);
  }
};

// Массовое обновление пользователей в кэше
export const bulkUpdateCache = (usersUpdates) => {
  try {
    const cachedUsers = loadFromCache();
    if (cachedUsers && Array.isArray(cachedUsers) && Array.isArray(usersUpdates)) {
      const updatedUsers = cachedUsers.map(user => {
        const update = usersUpdates.find(u => u.id === user.id);
        return update ? { ...user, ...update } : user;
      });
      saveToCache(updatedUsers);
    }
  } catch (error) {
    console.error("Error bulk updating cache:", error);
  }
};

// Получение статистики кэша
export const getCacheStats = () => {
  try {
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (!cachedData) return null;
    
    const { timestamp, users } = JSON.parse(cachedData);
    const age = Date.now() - timestamp;
    const ageHours = (age / (1000 * 60 * 60)).toFixed(1);
    const isExpired = age >= CACHE_DURATION;
    
    return {
      userCount: users?.length || 0,
      age: age,
      ageHours: ageHours,
      isExpired: isExpired,
      timestamp: new Date(timestamp).toLocaleString()
    };
  } catch (error) {
    console.error("Error getting cache stats:", error);
    return null;
  }
};

// Проверка валидности кэша
export const isCacheValid = () => {
  try {
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (!cachedData) return false;
    
    const { timestamp, users } = JSON.parse(cachedData);
    const isValid = Date.now() - timestamp < CACHE_DURATION && Array.isArray(users);
    return isValid;
  } catch (error) {
    return false;
  }
};